import React, { useState, useEffect } from 'react'
import useTitle from "../../../hooks/useTitle"
import {
    PlusIcon,
    MinusIcon,
    PencilSquareIcon,
  } from "@heroicons/react/24/outline"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLight2 from "../../../utils/EtContentEditorLight2"
import EtBtnFormBasic from "../../../components/common/buttons/EtBtnFormBasic"
import { useNavigate } from "react-router"
import { useCreateNewUserImprintCapabilitiesSoftSkillMutation, useGetUserImprintCapabilitiesSoftSkillsQuery } from './userImprintCapabilitiesSoftSkillsApiSlice'
import { useGetCapabilitiesSoftSkillTypesQuery } from '../../capabilitiesSoftSkillTypes/capabilitiesSoftSkillTypesApiSlice'
import { produce } from "immer";

const EtUpdateUserImprintCapabilitiesSoftSkillComp = ({
  userId, 
  userOrgId, 
  triggeringUrl, 
  onClose
}) => {
  // First Created - 22 Nov 2024
  // Author - Atul Pandey
  // Inherited from the EtCreateUserImprintCapabilitiesIndustry component

  // 0 - Set Page title & variables
  useTitle('User Imprint Cpabilities')


  // 1a - set page navigations and seed variables (these will be props)
  const navigate = useNavigate()
  
  // 1b - Initialize state variables for the editor key and value
  const [editorKey, setEditorKey] = useState("capabilitiesSoftSkillDesc-" + Date.now())
  const [editorValue, setEditorValue] = useState("")
  const [showCreateUserImprintCapabilitiesSoftSkill, setShowCreateUserImprintCapabilitiesSoftSkill] = useState(false)
  const [selectedSoftSkill, setSelectedSoftSkill] = useState(null); // 20 Nov 2024

  // 2a - set the mutation to create the new user imprint capabilitiesSoftSkill data
  const [createNewUserImprintCapabilitiesSoftSkill, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useCreateNewUserImprintCapabilitiesSoftSkillMutation()

  // 2b - Get capability softSkill types
  const { data: softSkillTypesData} = useGetCapabilitiesSoftSkillTypesQuery()
  const softSkillTypes = softSkillTypesData?.ids?.map(id => softSkillTypesData?.entities[id])
  console.log('SoftSkill Types', softSkillTypes)

  // 2c - Group SoftSkill Types by focus, domain, function, and tech area
  // const groupedSoftSkillTypes = softSkillTypes?.reduce((result, type) => {
  //   const focus = type.capabilitiesTechAreaType?.capabilitiesFunctionType?.capabilitiesDomainType?.capabilitiesDomainFocus || "Other";
  //   const domainName = type.capabilitiesTechAreaType?.capabilitiesFunctionType?.capabilitiesDomainType?.capabilitiesDomainTypeName || "Unknown Domain";
  //   const functionName = type.capabilitiesTechAreaType?.capabilitiesFunctionType?.capabilitiesFunctionTypeName || "Unknown Function";
  //   const techAreaName = type.capabilitiesTechAreaType?.capabilitiesTechAreaTypeName || "Unknown TechArea";
  
  //   if (!result[focus]) {
  //     result[focus] = {};
  //   }
  
  //   if (!result[focus][domainName]) {
  //     result[focus][domainName] = {};
  //   }
  
  //   if (!result[focus][domainName][functionName]) {
  //     result[focus][domainName][functionName] = {};
  //   }
  
  //   if (!result[focus][domainName][functionName][techAreaName]) {
  //     result[focus][domainName][functionName][techAreaName] = [];
  //   }
  
  //   result[focus][domainName][functionName][techAreaName].push(type);
  
  //   return result;
  //   }, {}) || {};
  
  //   console.log('groupedSoftSkillTypes', groupedSoftSkillTypes);

  // 2d - Get existing capabilities softSkill Data from the backend
  const { data: userImprintCapabilitiesSoftSkillData} = useGetUserImprintCapabilitiesSoftSkillsQuery()
  const userImprintCapabilitiesSoftSkills = userImprintCapabilitiesSoftSkillData?.ids
  ?.map(id => userImprintCapabilitiesSoftSkillData?.entities[id])
  ?.filter(item=> item.userId._id === userId && item.userOrgId._id === userOrgId) || []
  console.log('userImprintCapabilties SoftSkill', userImprintCapabilitiesSoftSkills)

  // 2e - Update the selectedSoftSkill state value based on the incoming data
  useEffect(() => {
    if (userImprintCapabilitiesSoftSkills?.length > 0) {
      setSelectedSoftSkill(userImprintCapabilitiesSoftSkills[0]);
    }
  }, [userImprintCapabilitiesSoftSkills]);
  
  // 3a - Initialize state variables for the form data
  const [formData, setFormData] = useState({
    userImprintCapabilitiesSoftSkillName: "",
    userImprintCapabilitiesSoftSkillDesc: "",
    userImprintCapabilitiesSoftSkillStats: [
      {
        softSkillType:null,
        yearsOfExperience: 0,
        proficiencyLevel: "Yet to Begin",
        numberOfEngagements:0,
        certifications: [
          {
            certificationName: "",
            certificationDesc: "",
            certificationDate: "",
            validTill: "",
          }
        ],
        recognitions: [
          {
            recognitionName: "",
            recognitionDesc: "",
            recognitionOrg: "",
            recognitionDate: "",
          }
        ],
      }
    ],
    userImprintCapabilitiesSoftSkillSelfNotes: [],
    aboutMe: "",
    pic: "",
  })

  // 3b - Use Effect to populate the formData based on the data from the backend
  useEffect(() => {
    if (selectedSoftSkill) {
      setFormData(
        produce((draft) => {
          draft.userImprintCapabilitiesSoftSkillName = selectedSoftSkill.userImprintCapabilitiesSoftSkillName || "";
          draft.userImprintCapabilitiesSoftSkillDesc = selectedSoftSkill.userImprintCapabilitiesSoftSkillDesc || "";
          draft.userImprintCapabilitiesSoftSkillStats = selectedSoftSkill.userImprintCapabilitiesSoftSkillStats?.map((stat) => ({
            ...stat,
            softSkillType: stat.softSkillType?._id || null,
          })) || [
            {
              softSkillType: null,
              yearsOfExperience: 0,
              proficiencyLevel: "Yet to Begin",
              numberOfEngagements: 0,
              certifications: [],
              recognitions: [],
            },
          ];
          draft.userImprintCapabilitiesSoftSkillSelfNotes = selectedSoftSkill.userImprintCapabilitiesSoftSkillSelfNotes || [];
          draft.aboutMe = selectedSoftSkill.aboutMe || "";
          draft.pic = "";
        })
      );
      setEditorValue(selectedSoftSkill.userImprintCapabilitiesSoftSkillDesc || "");
    }
  }, [selectedSoftSkill]);
 
  // 5 - Handle form field changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
  setFormData(
    produce((draft) => {
      if (name === "pic") {
        draft.pic = files[0];
      } else {
        draft[name] = value;
      }
    })
  );
  }

  // 5a - Handle editor value change
  const handleEditorChange = (content) => {
    setEditorValue(content)
    setFormData(prevState => ({ ...prevState, userImprintCapabilitiesSoftSkillDesc: content }))
  }

  // 5b - Generic Handle Add Field SoftSkill
  const handleAdd = (fieldName, newItem) => {
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: [...prevState[fieldName], newItem]
    }))
  }

  // 5c - Generic Handle Remove Field SoftSkill
  const handleRemove = (fieldName, index) => {
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: prevState[fieldName].filter((_, i) => i !== index)
    }))
  }

  // 5d - SoftSkill to convert pic to Base64
  const convertPicToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    }); 
 

  // 6 - Form submit logic
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Step 1: Handle the pic conversion separately
    const picBase64 = formData.pic ? await convertPicToBase64(formData.pic) : undefined;
  
    // Step 2: Use Immer to produce an immutable copy
    const payload = produce(formData, (draft) => {
      draft.userId = userId;
      draft.userOrgId = userOrgId;
      // Trim and clean up values within the draft object
      draft.userImprintCapabilitiesSoftSkillName =
        draft.userImprintCapabilitiesSoftSkillName?.trim() || undefined;
      draft.userImprintCapabilitiesSoftSkillDesc =
        draft.userImprintCapabilitiesSoftSkillDesc?.trim() || undefined;
  
      draft.userImprintCapabilitiesSoftSkillStats.forEach((stat) => {
        stat.certifications = stat.certifications.filter((cert) =>
          Object.values(cert).some((value) => value !== "")
        );
        stat.recognitions = stat.recognitions.filter((rec) =>
          Object.values(rec).some((value) => value !== "")
        );
      });
  
      draft.userImprintCapabilitiesSoftSkillSelfNotes =
        draft.userImprintCapabilitiesSoftSkillSelfNotes.filter(
          (note) => note.userImprintCapabilitiesSoftSkillSelfNoteText.trim() !== ""
        );
      draft.aboutMe = draft.aboutMe?.trim() || undefined;
      // Add the Base64 pic separately
      draft.pic = picBase64;
      
    });
  
    try {
      // Call the mutation
      await createNewUserImprintCapabilitiesSoftSkill(payload).unwrap();
  
      // Reset form and navigate on success
      setFormData({
        userImprintCapabilitiesSoftSkillName: "",
        userImprintCapabilitiesSoftSkillDesc: "",
        userImprintCapabilitiesSoftSkillStats: [
          {
            softSkillType: null,
            yearsOfExperience: 0,
            proficiencyLevel: "Yet to Begin",
            numberOfEngagements: 0,
            certifications: [],
            recognitions: [],
          },
        ],
        userImprintCapabilitiesSoftSkillSelfNotes: [],
        aboutMe: "",
        pic: "",
      });
      if (onClose) {
        onClose(); // 21 Nov 2024
      } else{
        navigate(triggeringUrl || '/userMagicHome');
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };
  
  // 7 - Reset form upon successful submission
  useEffect(() => {
    if(isSuccess) {
      setFormData({
        userImprintCapabilitiesSoftSkillName: "",
      userImprintCapabilitiesSoftSkillDesc: "",
      userImprintCapabilitiesSoftSkillStats: [
        {
          softSkillType:null,
          yearsOfExperience: 0,
          proficiencyLevel: "Yet to Begin",
          numberOfEngagements:0,
          certifications: [
            {
              certificationName: "",
              certificationDesc: "",
              certificationOrg:"",
              certificationDate: "",
              validTill: "",
            }
          ],
          recognitions: [
            {
              recognitionName: "",
              recognitionDesc: "",
              recognitionOrg: "",
              recognitionDate: "",
            }
          ],
        }
      ],
      userImprintCapabilitiesSoftSkillSelfNotes: [],
      aboutMe: "",
      pic: "",
      })
      // setShowCreateUserImprintCapabilitiesSoftSkill(false)
      if (onClose) {
        onClose(); // 21 Nov 2024
      } else{
        navigate(triggeringUrl || '/userMagicHome');
      }
    }
  
    },[isSuccess,navigate])

  // 8 - Handle Cancel
  const handleCancel = () => {
    // setShowCreateUserImprintCapabilitiesSoftSkill(false)
    if (onClose) {
      onClose(); // 21 Nov 2024
    } else{
      navigate(triggeringUrl || '/userMagicHome');
    }
  }

  // 9 - Handle Clear
  const handleClear = () => {
    setFormData(
      produce((draft) => {
        draft.userImprintCapabilitiesSoftSkillName = "";
        draft.userImprintCapabilitiesSoftSkillDesc = "";
        draft.userImprintCapabilitiesSoftSkillStats = [
          {
            softSkillType: null,
            yearsOfExperience: 0,
            proficiencyLevel: "Yet to Begin",
            numberOfEngagements: 0,
            certifications: [],
            recognitions: [],
          },
        ];
        draft.userImprintCapabilitiesSoftSkillSelfNotes = [];
        draft.aboutMe = "";
        draft.pic = "";
      })
    );
  };
 
  return (
    <div className="mx-auto m-1 p-1 bg-white rounded shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}
      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <p className="p-1 m-1 font-bold font-poppins text-xl">Create User Imprint Capabilities - SoftSkill</p>

      
      <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">
      {/* 1. userImprintCapabilitiesSoftSkillName */}
      <div id="userImprintCapabilitiesSoftSkillName" className="bg-gray-100 rounded p-1 m-1">
        <label htmlFor="userImprintCapabilitiesSoftSkillName" className="block text-gray-600 text-sm font-bold mb-1">
          Capability Name (Optional)
        </label>
        <input
          type="text"
          name="userImprintCapabilitiesSoftSkillName"
          placeholder="Enter a headline or title"
          value={formData.userImprintCapabilitiesSoftSkillName}
          onChange={handleChange}
          className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
        />
      </div>
      
      {/* 2. userImprintCapabilitiesSoftSkillDesc */}
      <div id="userImprintCapabilitiesSoftSkillDesc" className="bg-gray-100 rounded p-1 m-1">
        <label htmlFor="userImprintCapabilitiesSoftSkillDesc" className="block text-gray-600 text-sm font-bold mb-1">
          Capability Description
        </label>
        <EtContentEditorLight2
          value={editorValue}
          handleChange={handleEditorChange}
          placeholderText="Write your capability description here!"
          editorKey={editorKey}
        />
      </div>

      {/* SoftSkill Stats Section */}

      <div className="flex justify-between items-center m-1 p-1">
        <p className="font-bold text-sm mb-2">SoftSkill Stats</p>
        <div className='flex justify-start items-center'>
          <p className='text-xs font-bold mx-1 px-1'>Add</p>
          <button
            type="button"
            className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
            onClick={() => {
              setFormData(
                produce((draft) => {
                  draft.userImprintCapabilitiesSoftSkillStats.push({
                    softSkillType: null,
                    yearsOfExperience: 0,
                    proficiencyLevel: "Yet to Begin",
                    numberOfEngagements: 0,
                    certifications: [],
                    recognitions: [],
                  });
                })
              );
            }}
          >
            <PlusIcon className="h-4 w-4 inline" /> 
          </button>
        </div>
        
      </div>

      {formData.userImprintCapabilitiesSoftSkillStats.map((stat, statIndex) => (
          <div key={statIndex} className="border rounded p-2 m-2 bg-gray-50">
            <div className="flex justify-between items-center mb-2">
                <p className="font-bold text-sm mb-2">SoftSkill Stat {statIndex + 1}</p>
                <div className='flex justify-start items-center'>
                  <p className='text-xs font-bold mx-1 px-1'>Remove</p>
                  <button
                    type="button"
                    className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                    onClick={() => {
                      setFormData(
                        produce((draft) => {
                          draft.userImprintCapabilitiesSoftSkillStats.splice(statIndex, 1);
                        })
                      );
                    }}
                  >
                    <MinusIcon className="h-4 w-4 inline" /> 
                  </button>
                </div>
                
            </div>

            {/* SoftSkill Type Dropdown */}
            <div id={`softSkillType-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
              <label htmlFor={`softSkillType-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                SoftSkill Type
              </label>
                <select
                id={`softSkillType-${statIndex}`}
                value={stat.softSkillType || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prevState) => {
                    const updatedStats = [...prevState.userImprintCapabilitiesSoftSkillStats];
                    updatedStats[statIndex].softSkillType = value;
                    return { ...prevState, userImprintCapabilitiesSoftSkillStats: updatedStats };
                  });
                }}
                className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
              >
                <option value="" disabled>
                  Select SoftSkill Type
                </option>
                {softSkillTypes?.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.capabilitiesSoftSkillTypeName}
                  </option>
                ))}
                </select>
               {/* <select
                    id={`softSkillType-${statIndex}`}
                    value={stat.softSkillType || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prevState) => {
                        const updatedStats = [...prevState.userImprintCapabilitiesSoftSkillStats];
                        updatedStats[statIndex].softSkillType = value;
                        return { ...prevState, userImprintCapabilitiesSoftSkillStats: updatedStats };
                      });
                    }}
                    className="w-full rounded p-2 border border-gray-300 focus:outline-none"
                  >
                    <option value="" disabled>
                      Select Tech Stack Type
                    </option>
                    {Object.entries(groupedSoftSkillTypes)?.map(([focus, domains]) =>
                      Object.entries(domains).flatMap(([domainName, functions]) =>
                        Object.entries(functions).flatMap(([functionName, techAreas]) =>
                          Object.entries(techAreas).map(([techAreaName, softSkills]) => (
                            <optgroup
                              key={`${focus}-${domainName}-${functionName}-${techAreaName}`}
                              label={`${focus} - ${domainName} - ${functionName} - ${techAreaName}`}
                            >
                              {softSkills.map((stack) => (
                                <option key={stack._id} value={stack._id}>
                                  {stack.capabilitiesSoftSkillTypeName}
                                </option>
                              ))}
                            </optgroup>
                          ))
                        )
                      )
                    )}
                  </select> */}

            </div>

              {/* Years of Experience */}
              <div id={`yearsOfExperience-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
                <label htmlFor={`yearsOfExperience-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                  Years of Experience
                </label>
                <input
                  type="number"
                  id={`yearsOfExperience-${statIndex}`}
                  name="yearsOfExperience"
                  min="0"
                  value={stat.yearsOfExperience || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10) || 0;
                    setFormData(
                      produce((draft) => {
                        draft.userImprintCapabilitiesSoftSkillStats[statIndex].yearsOfExperience = value;
                      })
                    );
                  }}
                  className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
                />
              </div>

              {/* Proficiency Level */}
              <div id={`proficiencyLevel-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
                <label htmlFor={`proficiencyLevel-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                  Proficiency Level
                </label>
                <select
                  id={`proficiencyLevel-${statIndex}`}
                  name="proficiencyLevel"
                  value={stat.proficiencyLevel || "Yet to Begin"}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(
                      produce((draft) => {
                        draft.userImprintCapabilitiesSoftSkillStats[statIndex].proficiencyLevel = value;
                      })
                    );
                  }}
                  className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
                >
                  <option value="Yet to Begin">Yet to Begin</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Pro">Pro</option>
                  <option value="Expert">Expert</option>
                  <option value="Leader">Leader</option>
                </select>
              </div>

              {/* Number of Engagements */}
              <div id={`numberOfEngagements-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
                <label htmlFor={`numberOfEngagements-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                  Number of Engagements
                </label>
                <input
                  type="number"
                  id={`numberOfEngagements-${statIndex}`}
                  name="numberOfEngagements"
                  min="0"
                  value={stat.numberOfEngagements || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10) || 0;
                    setFormData(
                      produce((draft) => {
                        draft.userImprintCapabilitiesSoftSkillStats[statIndex].numberOfEngagements = value;
                      })
                    );
                  }}
                  className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
                />
              </div>

              {/* Certifications Section */}
              <div id={`certifications-${statIndex}`} className="border rounded p-2 m-2 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                    <p className="font-bold text-sm">Certifications</p>
                    <div className='flex justify-start items-center'>
                      <p className='text-xs font-bold mx-1 px-1'>Add</p>
                      <button
                          type="button"
                          className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
                          onClick={() => {
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesSoftSkillStats[statIndex].certifications.push({
                                  certificationName: "",
                                  certificationDesc: "",
                                  certificationOrg:"",
                                  certificationDate: "",
                                  validTill: "",
                                });
                              })
                            );
                          }}
                        >
                        <PlusIcon className="h-4 w-4 inline" />
                      </button>

                    </div>
                  
                </div>

                {/* Certifications List */}
                {stat.certifications.map((cert, certIndex) => (
                  <div key={`${statIndex}-${certIndex}`} className="p-2 border rounded mb-2 bg-white">
                    <div className="flex justify-between items-center">
                        <p className="font-bold text-sm">Certification {certIndex + 1}</p>
                        <div className='flex justify-start items-center'>
                            <p className='text-xs font-bold mx-1 px-1'>Remove</p>
                            <button
                            type="button"
                            className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                            onClick={() => {
                              setFormData(
                                produce((draft) => {
                                  draft.userImprintCapabilitiesSoftSkillStats[statIndex].certifications.splice(certIndex, 1);
                                })
                              );
                            }}
                          >
                            <MinusIcon className="h-4 w-4 inline" />
                          </button>
                        </div>
                      
                    </div>

                    {/* Certification Fields */}
                    <input
                      type="text"
                      placeholder="Certification Name"
                      value={cert.certificationName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(
                          produce((draft) => {
                            draft.userImprintCapabilitiesSoftSkillStats[statIndex].certifications[certIndex].certificationName = value;
                          })
                        );
                      }}
                      className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300 mt-2"
                    />

                    <EtContentEditorLight2
                      value={cert.certificationDesc}
                      handleChange={(content) => {
                        setFormData(
                          produce((draft) => {
                            draft.userImprintCapabilitiesSoftSkillStats[statIndex].certifications[certIndex].certificationDesc = content;
                          })
                        );
                      }}
                      placeholderText="Write certification details here..."
                      editorKey={`certificationDesc-${statIndex}-${certIndex}`}
                    />

                    <div className="flex justify-between items-center">
                      
                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`certificationOrg-${statIndex}-${certIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Certifying Organization:
                        </label>
                        <input
                          type="text"
                          id={`certificationOrg-${statIndex}-${certIndex}`}
                          value={cert.certificationOrg || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesSoftSkillStats[statIndex].certifications[certIndex].certificationOrg = value;
                              })
                            );
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>

                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`certificationDate-${statIndex}-${certIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Certification Date
                        </label>
                        <input
                          type="date"
                          id={`certificationDate-${statIndex}-${certIndex}`}
                          value={cert.certificationDate ? cert.certificationDate.split('T')[0] : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesSoftSkillStats[statIndex].certifications[certIndex].certificationDate = value;
                              })
                            );
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>

                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`validTill-${statIndex}-${certIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Valid Till
                        </label>
                        <input
                          type="date"
                          id={`validTill-${statIndex}-${certIndex}`}
                          value={cert.validTill ? cert.validTill.split('T')[0] : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(
                              produce((draft) => {
                                const cert = draft.userImprintCapabilitiesSoftSkillStats[statIndex].certifications[certIndex];
                                if (new Date(value) >= new Date(cert.certificationDate)) {
                                  cert.validTill = value;
                                }
                              })
                            );
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recognitions Section */}
              <div id={`recognitions-${statIndex}`} className="border rounded p-2 m-2 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                    <p className="font-bold text-sm">Recognitions</p>
                    <div className='flex justify-start items-center'>
                        <p className='text-xs font-bold mx-1 px-1'>Add</p>
                        <button
                          type="button"
                          className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
                          onClick={() => {
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesSoftSkillStats[statIndex].recognitions.push({
                                  recognitionName: "",
                                  recognitionDesc: "",
                                  recognitionOrg: "",
                                  recognitionDate: "",
                                });
                              })
                            );
                          }}
                        >
                          <PlusIcon className="h-4 w-4 inline" />
                        </button>
                    </div>
                  
                </div>

                {/* Recognitions List */}
                {stat.recognitions.map((rec, recIndex) => (
                  <div key={`${statIndex}-${recIndex}`} className="p-2 border rounded mb-2 bg-white">
                    <div className="flex justify-between items-center">
                        <p className="font-bold text-sm">Recognition {recIndex + 1}</p>
                        <div className='flex justify-start items-center'>
                            <p className='text-xs font-bold mx-1 px-1'>Remove</p>
                            <button
                            type="button"
                            className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                            onClick={() => {
                              setFormData(
                                produce((draft) => {
                                  draft.userImprintCapabilitiesSoftSkillStats[statIndex].recognitions.splice(recIndex, 1);
                                })
                              );
                            }}
                          >
                            <MinusIcon className="h-4 w-4 inline" />
                          </button>
                        </div>

                    </div>

                    {/* Recognition Fields */}
                    <input
                      type="text"
                      placeholder="Recognition Name"
                      value={rec.recognitionName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(
                          produce((draft) => {
                            draft.userImprintCapabilitiesSoftSkillStats[statIndex].recognitions[recIndex].recognitionName = value;
                          })
                        );
                      }}
                      className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300 mt-2"
                    />

                    <EtContentEditorLight2
                      value={rec.recognitionDesc}
                      handleChange={(content) => {
                        setFormData(
                          produce((draft) => {
                            draft.userImprintCapabilitiesSoftSkillStats[statIndex].recognitions[recIndex].recognitionDesc = content;
                          })
                        );
                      }}
                      placeholderText="Write recognition details here..."
                      editorKey={`recognitionDesc-${statIndex}-${recIndex}`}
                    />

                    <div className="flex justify-between items-center">
                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`recognitionOrg-${statIndex}-${recIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Recognizing Org
                        </label>
                        <input
                          type="text"
                          id={`recognitionOrg-${statIndex}-${recIndex}`}
                          placeholder="Enter recognizing organization"
                          value={rec.recognitionOrg}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesSoftSkillStats[statIndex].recognitions[recIndex].recognitionOrg = value;
                              })
                            );
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>

                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`recognitionDate-${statIndex}-${recIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Recognition Date
                        </label>
                        <input
                          type="date"
                          id={`recognitionDate-${statIndex}-${recIndex}`}
                          value={rec.recognitionDate ? rec.recognitionDate.split('T')[0] : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesSoftSkillStats[statIndex].recognitions[recIndex].recognitionDate = value;
                              })
                            );
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          </div>
      ))}

      {/* Notes Section */}
      <div id="selfNotes" className="border rounded p-2 m-2 bg-gray-50">
        <div className="flex justify-between items-center mb-2">
            <p className="font-bold text-sm">Notes</p>
            <div className='flex justify-start items-center'>
                <p className='text-xs font-bold mx-1 px-1'>Add</p>
                <button
                type="button"
                className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
                onClick={() => {
                  setFormData(
                    produce((draft) => {
                      draft.userImprintCapabilitiesSoftSkillSelfNotes.push({
                        userImprintCapabilitiesSoftSkillSelfNoteText: "",
                        userImprintCapabilitiesSoftSkillSelfNoteDate: new Date().toISOString(),
                        userImprintCapabilitiesSoftSkillSelfNoteUpdateDate: null,
                      });
                    })
                  );
                }}
              >
                <PlusIcon className="h-4 w-4 inline" />
              </button>
            </div>
          
        </div>

        {formData.userImprintCapabilitiesSoftSkillSelfNotes?.map((note, noteIndex) => (
          <div key={noteIndex} className="p-2 border rounded mb-2 bg-white">
            <div className="flex justify-between items-center">
                  <p className="font-bold text-sm">Self Note: {noteIndex + 1}</p>
              <div className='flex justify-start items-center'>
                  <p className='text-xs font-bold mx-1 px-1'>Remove</p>
                  <button
                    type="button"
                    className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                    onClick={() => {
                      setFormData(
                        produce((draft) => {
                          draft.userImprintCapabilitiesSoftSkillSelfNotes.splice(noteIndex, 1);
                        })
                      );
                    }}
                  >
                    <MinusIcon className="h-4 w-4 inline" />
                  </button>
              </div>
              
            </div>

            {/* Note Text */}
            <EtContentEditorLight2
              value={note.userImprintCapabilitiesSoftSkillSelfNoteText}
              handleChange={(content) => {
                setFormData(
                  produce((draft) => {
                    draft.userImprintCapabilitiesSoftSkillSelfNotes[noteIndex].userImprintCapabilitiesSoftSkillSelfNoteText = content;
                    draft.userImprintCapabilitiesSoftSkillSelfNotes[noteIndex].userImprintCapabilitiesSoftSkillSelfNoteUpdateDate = new Date().toISOString();
                  })
                );
              }}
              placeholderText="Write your note here..."
              editorKey={`note-${noteIndex}`}
            />

            {/* Note Date (Read-Only) */}
            <p className="text-xs text-gray-500 mt-2">
              Created on: {new Date(note.userImprintCapabilitiesSoftSkillSelfNoteDate).toLocaleDateString()}
              {note.userImprintCapabilitiesSoftSkillSelfNoteUpdateDate && (
                <span>
                  , Updated on: {new Date(note.userImprintCapabilitiesSoftSkillSelfNoteUpdateDate).toLocaleDateString()}
                </span>
              )}
            </p>
          </div>
        ))}
      </div>

      <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Update"}
                type={"create"}
                handleClick={handleSubmit}
              />
              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={handleClear}
              />
              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>

    </form>

    </div>
  )
}

export default EtUpdateUserImprintCapabilitiesSoftSkillComp